import { bindActionCreators } from "@reduxjs/toolkit";
import { connect } from "react-redux";
import accountActions from "../../../store/actions/accounts/account.actions";
import accountService from "../../../services/accounts/account.service";
import { Link, useSearchParams } from "react-router-dom";

import "../../../styles/components/dialogs/accounts/manageAccountsDialogComponent.scss";
import AlertService from "../../../services/alerts/alert.service";
import validationHelper from "../../../helpers/validation.helper";
import { useEffect, useState, useCallback } from "react";
import TextInputElementComponent from "../../../components/general/elements/inputElements/TextInputElementComponent";
import textInputModel from "../../../models/general/elements/inputElements/textInput.model";
import useDelayedFlag from "../../../hooks/useDelayedFlag";
import { SkeletonTable } from "../../../components/general/skeleton";

export const ManageBusinessAccountsDialogComponent = ({ accountActions, accounts }) => {
  const [, setSearchParams] = useSearchParams();
  const [searchTerm, setSearchTerm] = useState("");

  // 0018: this dialog fetches via a Redux action (not a React Query hook), so it has no
  // isLoading flag of its own. Track ONLY the initial mount load locally to drive the
  // skeleton; post-mutation refreshes (handleToggleActive/handleDelete) reuse fetchAccounts
  // without flipping this, so toggling a row never blanks the whole table.
  const [isInitialLoading, setIsInitialLoading] = useState(true);

  const fetchAccounts = useCallback(async () => {
    try {
      await accountActions.getUsersByBusinessCustomer(); // Adjust the action to fetch all accounts
    } catch (error) {
      console.error("Error fetching accounts:", error);
    }
  }, [accountActions]);

  useEffect(() => {
    setIsInitialLoading(true);
    fetchAccounts().finally(() => setIsInitialLoading(false));
  }, [fetchAccounts]);

  // Show the skeleton only while the FIRST load is in flight AND there is nothing to show yet;
  // on a re-open with accounts already in the Redux store, keep showing them (no flash).
  const showSkeleton = useDelayedFlag(isInitialLoading) && !accounts?.length;

  // 0007: open the add/edit dialog in EDIT mode (prefilled). Mirrors the agent pattern
  // (action=add + id), so no new dialog route is needed.
  const handleUpdate = (id) => {
    const queryParams = new URLSearchParams();
    queryParams.set("d", "account");
    queryParams.set("action", "add");
    queryParams.set("id", id);

    setSearchParams(queryParams);
  };

  // 0007: suspend/activate a business login account. Backed by the admin-only
  // PUT /Account/{id}/Active endpoint; the 0006 keystone 401s the account on its next request.
  const handleToggleActive = async (account) => {
    try {
      const response = await accountService.setBusinessAccountActive(account.id, !account.isActive);
      if (!validationHelper.validateHttpResponse(response.data)) {
        AlertService.error("עדכון סטטוס החשבון נכשל", "אנא נסה שנית מאוחר יותר");
        return;
      }
      fetchAccounts();
    } catch (error) {
      AlertService.error("עדכון סטטוס החשבון נכשל", "אנא נסה שנית מאוחר יותר");
      console.error("Error toggling business account active state:", error);
    }
  };

  const getAccountPersonName = (account) => {
    return `${account.firstName || ""} ${account.lastName || ""}`.trim();
  };

  const handleDelete = async (email) => {
    const userChoice = await AlertService.confirm("האם אתה בטוח שברצונך למחוק חשבון זה?", "שים לב, פעולה זו בלתי הפיכה", "כן מחק", "בטל");
    if (!userChoice) return;

    const response = await accountActions.deleteAccount(email);
    if (!validationHelper.validateHttpResponse(response)) {
      AlertService.error("מחיקת חשבון נכשלה. נסה שוב.", "אנא נסה שנית מאוחר יותר");
      return;
    }
    AlertService.success("חשבון נמחק בהצלחה");
    fetchAccounts();
  };

  const getNewAccountContainerElement = () => {
    return (
      <div className="newAccountContainer">
        <Link to="?d=account&action=add" className="addAccountButton primaryButton">
          הוספה
        </Link>
      </div>
    );
  };

  const getSearchInput = () => {
    const inputProps = {
      ...textInputModel,
      name: "searchInput",
      value: searchTerm,
      placeholder: "חפש חשבון...",
      onChange: (e) => setSearchTerm(e.target.value),
      icon: <i className="fa-solid fa-magnifying-glass"></i>,
    };
    return <TextInputElementComponent props={inputProps} />;
  };

  const getExistingAccountsContainerElement = () => {
    // Filter accounts based on search term
    const filteredAccounts = accounts?.filter((account) => {
      if (!searchTerm) return true;
      const searchLower = searchTerm.toLowerCase();
      const email = account.email?.toLowerCase() || "";
      const name = account.businessCustomer?.lastName?.toLowerCase() || "";
      const customerId = account.businessCustomer?.customerId?.toString() || "";
      return email.includes(searchLower) || name.includes(searchLower) || customerId.includes(searchLower);
    });

    return (
      <div className="existingAccountTable">
        <div className="tableHeader">
          <div>דוא"ל</div>
          <div>קוד חברה</div>
          <div>שם</div>
          <div>פעולות</div>
        </div>
        <div className="tableBody">
          {showSkeleton && <SkeletonTable rows={6} cols={4} showHeader={false} />}
          {!showSkeleton && filteredAccounts?.map((account) => (
            <div key={account.id} className="tableRow">
              <div className="accountEmail">
                <span className="mobileLabel">דוא"ל:</span>
                <span className="accountValue">{account.email}</span>
              </div>
              <div className="accountUsername">
                <span className="mobileLabel">קוד חברה:</span>
                <span className="accountValue">{account.businessCustomer.customerId}</span>
              </div>
              <div className="accountUsername">
                <span className="mobileLabel">שם:</span>
                <span className="accountValue">{account.businessCustomer.lastName}</span>
                {/* 0007: the PERSON on this login (separate from the Rivhit company name above).
                    Several people can share one company, each with their own login + details. */}
                {getAccountPersonName(account) && <span className="accountSubValue">{getAccountPersonName(account)}</span>}
              </div>
              <div className="accountActions">
                <span className="mobileLabel">פעולות:</span>
                <div className="actionsButtonsContainer">
                  {/* 0007: suspend/activate — green pause (active) / red play (suspended). */}
                  <button
                    className={`actionButton statusButton ${account.isActive ? "isActive" : "isSuspended"}`}
                    title={account.isActive ? "השהה חשבון" : "הפעל חשבון"}
                    onClick={() => handleToggleActive(account)}
                  >
                    <i className={`fa-solid ${account.isActive ? "fa-pause" : "fa-play"}`}></i>
                  </button>
                  <button className="actionButton updateButton" title="עריכת פרטים" onClick={() => handleUpdate(account.id)}>
                    <i className="fa-solid fa-pen"></i>
                  </button>
                  <button className="actionButton deleteButton" title="מחיקה" onClick={() => handleDelete(account.email)}>
                    <i className="fa-solid fa-trash"></i>
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  return (
    <div className="dialogContainer manageAccountsDialogContainer">
      <h1>ניהול חשבונות</h1>
      {getNewAccountContainerElement()}
      <div className="searchContainer">{getSearchInput()}</div>
      {getExistingAccountsContainerElement()}
    </div>
  );
};

const mapStateToProps = (state) => ({
  accounts: state.accountReducer.usersByBusiness, // Subscribe to accounts state from the reducer
});

const mapDispatchToProps = (dispatch) => {
  return {
    accountActions: bindActionCreators(accountActions, dispatch),
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(ManageBusinessAccountsDialogComponent);
